/**
 * NoteToProjectModal — slash-action «Примечание к сделке».
 *
 * Шаги:
 *  1) Поиск производственной карточки (id/имя/адрес/amo_lead_id)
 *  2) Текст примечания
 *  3) Тоггл «Дублировать в AmoCRM» (отключён если у сделки нет amo_lead_id)
 *  4) Отправка → POST /api/production/{id}/notes → +
 *     дублирование в текущий чат как обычное сообщение со ссылкой.
 */
import { crmDealUrl, crmProductionUrl } from "@/lib/crmLink";
import { useEffect, useRef, useState } from "react";
import { Icon } from "@/components/prototype/Icon";
import { VoiceNoteRecorder } from "@/components/VoiceNoteRecorder";
import { sendVoiceNote } from "@/api/dashboard";

interface ProjectHit {
  /** null если сделка пришла из amo_lead_drive и production_project ещё не создан.
   *  В этом случае при пике вызывается /production/ensure-from-amo. */
  id: number | null;
  client_name: string;
  location: string | null;
  status: string | null;
  amo_lead_id: number | null;
  canonical_stage: string | null;
  /** Если Amo нашёл сделку по контакту (не по name) — имя контакта.
   *  UI показывает: «найдено по контакту: <имя>». */
  matched_contact?: string | null;
}

interface SubmitResult {
  project: { id: number; client_name: string };
  amo: { synced: boolean; error: string | null; note_id: number | null; lead_id: number | null };
  note_index: number;
  note_ts: string;
}

const STATUS_LABEL: Record<string, string> = {
  in_progress: "В работе",
  awaiting: "Ожидает",
  completed: "Завершён",
  cancelled: "Отменён",
  lead_stub: "Выезд",
};

/** Маппинг канонических Amo-стадий → цвет/группа бейджа.
 *  group: pre = до договора (Выезды и т.п.), contract = договор, done = выполнено. */
function stageGroup(canonical: string | null, status: string | null): "pre" | "contract" | "done" | null {
  if (canonical === "Договор заключён") return "contract";
  if (canonical === "Договор отложенный") return "contract";
  if (status === "completed") return "done";
  if (status === "in_progress") return "contract";
  if (status === "lead_stub") return "pre";
  return null;
}

async function searchProjects(q: string): Promise<ProjectHit[]> {
  const base = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api";
  // limit=200 — теперь показываем сделки ВО ВСЕХ этапах включая Тёплые
  // и Квалы. С учётом фильтрации на клиенте по табам — нужно достаточно.
  const params = new URLSearchParams({ limit: "200" });
  if (q.trim()) params.set("q", q.trim());
  const res = await fetch(`${base}/production/search?${params}`, { credentials: "include" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  return data.items as ProjectHit[];
}

async function submitNote(
  projectId: number, text: string, syncAmo: boolean, attachmentIds: number[],
): Promise<SubmitResult> {
  const base = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api";
  const res = await fetch(`${base}/production/${projectId}/notes`, {
    method: "POST", credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      text, sync_amo: syncAmo,
      attachment_ids: attachmentIds,
      from_messenger: true,
    }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  return res.json();
}

interface PendingFile {
  tempId: string;
  file: File;
  loaded: number;
  total: number;
  attachmentId: number | null;
  error: string | null;
}

async function uploadOne(
  file: File, onProgress: (loaded: number, total: number) => void,
): Promise<{ id: number; filename: string }> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const base = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api";
    xhr.open("POST", `${base}/chat/upload`);
    xhr.withCredentials = true;
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(e.loaded, e.total); };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try { resolve(JSON.parse(xhr.responseText)); }
        catch (e) { reject(e); }
      } else reject(new Error(`HTTP ${xhr.status}`));
    };
    xhr.onerror = () => reject(new Error("Network error"));
    const fd = new FormData();
    fd.append("file", file);
    xhr.send(fd);
  });
}

export function NoteToProjectModal({
  onClose, onSentToChat,
}: {
  onClose: () => void;
  /** Callback с готовым текстом сообщения для отправки в текущий чат.
   *  Родитель сам выбирает как послать — через свой sendMut. */
  onSentToChat: (text: string) => Promise<void>;
}) {
  const [step, setStep] = useState<"pick" | "compose">("pick");
  const [query, setQuery] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  // stageFilter: "all" | canonical_stage (точная строка из Amo)
  const [stageFilter, setStageFilter] = useState<string>("all");
  const [results, setResults] = useState<ProjectHit[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [picked, setPicked] = useState<ProjectHit | null>(null);
  const [text, setText] = useState("");
  const [syncAmo, setSyncAmo] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [files, setFiles] = useState<PendingFile[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const startUpload = (raw: File[]) => {
    const newOnes: PendingFile[] = raw.map(f => ({
      tempId: `tmp-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      file: f, loaded: 0, total: f.size, attachmentId: null, error: null,
    }));
    setFiles(prev => [...prev, ...newOnes]);
    for (const pf of newOnes) {
      uploadOne(pf.file, (loaded, total) => {
        setFiles(prev => prev.map(p => p.tempId === pf.tempId ? { ...p, loaded, total } : p));
      })
        .then(att => {
          setFiles(prev => prev.map(p => p.tempId === pf.tempId
            ? { ...p, attachmentId: att.id, loaded: pf.total } : p));
        })
        .catch(err => {
          setFiles(prev => prev.map(p => p.tempId === pf.tempId
            ? { ...p, error: (err as Error).message } : p));
        });
    }
  };

  const removeFile = (tempId: string) => {
    setFiles(prev => prev.filter(p => p.tempId !== tempId));
  };

  // Paste-from-clipboard
  useEffect(() => {
    if (step !== "compose") return;
    const onPaste = (e: ClipboardEvent) => {
      if (!e.clipboardData) return;
      const raw: File[] = [];
      for (const item of Array.from(e.clipboardData.items)) {
        if (item.kind === "file") {
          const f = item.getAsFile();
          if (f) raw.push(f);
        }
      }
      if (raw.length > 0) {
        e.preventDefault();
        startUpload(raw);
      }
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [step]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(query), 200);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    searchProjects(debouncedQ)
      .then(r => { if (!cancelled) setResults(r); })
      .catch(e => { if (!cancelled) setError(String(e?.message ?? e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [debouncedQ]);

  useEffect(() => {
    if (step === "pick") setTimeout(() => inputRef.current?.focus(), 50);
    if (step === "compose") setTimeout(() => textareaRef.current?.focus(), 50);
  }, [step]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const canSync = picked?.amo_lead_id != null;
  const effectiveSync = canSync && syncAmo;

  const [ensuring, setEnsuring] = useState(false);
  const onPick = async (h: ProjectHit) => {
    if (h.id != null) {
      setPicked(h);
      setSyncAmo(h.amo_lead_id != null);
      setStep("compose");
      return;
    }
    // id == null → сделка пришла из amo_lead_drive, нужно создать lead_stub
    if (h.amo_lead_id == null) {
      alert("Нет ни project_id, ни amo_lead_id — невозможно создать примечание.");
      return;
    }
    setEnsuring(true);
    try {
      const base = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api";
      const res = await fetch(`${base}/production/ensure-from-amo/${h.amo_lead_id}`, {
        method: "POST", credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
      const data = await res.json();
      const realId = Number(data.id);
      setPicked({ ...h, id: realId });
      setSyncAmo(true);
      setStep("compose");
    } catch (e) {
      alert(`Не удалось подготовить сделку: ${(e as Error).message ?? e}`);
    } finally {
      setEnsuring(false);
    }
  };

  const allFilesUploaded = files.every(f => f.attachmentId != null && !f.error);
  const hasContent = text.trim().length > 0 || files.some(f => f.attachmentId != null);

  async function onVoiceSend(args: { file: Blob; durationSec: number }) {
    if (!picked) { alert("Сначала выберите сделку"); return; }
    if (!picked.amo_lead_id) {
      alert("У выбранной сделки нет amo_lead_id — голосовое не отправить.");
      return;
    }
    try {
      const res = await sendVoiceNote(picked.amo_lead_id, args.file, args.durationSec, {
        category: "visits", propagateToProduction: true,
      });
      const projectUrl = crmDealUrl(picked.amo_lead_id) ?? crmProductionUrl(picked.id) ?? "";
      const projectLabel = picked.client_name + (picked.location ? ` (${picked.location})` : "");
      const amoMark = res.amo_note_id ? "✅" : "⚠️ не отправлено";
      const transcriptLine = res.transcript ? `\n\n«${res.transcript}»` : "";
      const chatText =
        `🎤 Голосовое примечание к сделке: ${projectLabel}\n` +
        `Ссылка: ${projectUrl}\n` +
        `Слушать: ${res.audio_url}` +
        `${transcriptLine}\n\n` +
        `AmoCRM: ${amoMark}`;
      await onSentToChat(chatText);
      onClose();
    } catch (e) {
      alert(`Не удалось: ${(e as Error).message}`);
    }
  }

  const onSubmit = async () => {
    if (!picked || !hasContent || submitting || !allFilesUploaded) return;
    const projectId = picked.id;
    if (projectId == null) return;
    setSubmitting(true);
    try {
      const attIds = files.map(f => f.attachmentId).filter((x): x is number => x != null);
      const result = await submitNote(projectId, text.trim(), effectiveSync, attIds);
      const projectUrl = crmDealUrl(picked.amo_lead_id) ?? crmProductionUrl(projectId) ?? "";
      const projectLabel = picked.client_name + (picked.location ? ` (${picked.location})` : "");
      const amoMark = effectiveSync
        ? (result.amo.synced ? "✅" : "⚠️ не отправлено")
        : (picked.amo_lead_id ? "⏭️ пропущено" : "—");
      const filesLine = files.length > 0
        ? `\n📎 Файлы: ${files.map(f => f.file.name).join(", ")}`
        : "";
      const chatText =
        `📝 Примечание к сделке: ${projectLabel}\n` +
        `Ссылка: ${projectUrl}\n\n` +
        `${text.trim()}${filesLine}\n\n` +
        `AmoCRM: ${amoMark}`;
      await onSentToChat(chatText);
      onClose();
    } catch (e) {
      alert(`Не удалось: ${(e as Error).message}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="ntpm-backdrop" onClick={onClose}>
      <div className="ntpm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ntpm-header">
          <div className="ntpm-header-text">
            <div className="ntpm-header-title">
              {step === "pick" ? "Выберите сделку" : "Примечание к сделке"}
            </div>
            {step === "compose" && picked && (
              <div className="ntpm-header-sub">
                {picked.client_name}
                {picked.location && <span className="ntpm-loc"> · {picked.location}</span>}
                {picked.amo_lead_id && <span className="ntpm-amo">Amo #{picked.amo_lead_id}</span>}
              </div>
            )}
          </div>
          <button className="ntpm-close" type="button" onClick={onClose} aria-label="Закрыть">
            <Icon name="x" size={18}/>
          </button>
        </div>

        {step === "pick" ? (
          <>
            <div className="ntpm-search-wrap">
              <Icon name="search" size={16}/>
              <input
                ref={inputRef}
                className="ntpm-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Имя клиента, адрес или номер Amo…"
              />
              {query && (
                <button type="button" className="ntpm-search-clear" onClick={() => setQuery("")}>
                  <Icon name="x" size={14}/>
                </button>
              )}
            </div>
            {/* Фильтр-чипы по стадиям. Динамически из реальных canonical_stage
                в результатах — покрывает ВСЕ воронки Amo автоматически
                (Тёплый лид / Квал / КП / Проект / Согласование выезда /
                Записаны на выезд / Выезд был / Договор отправлен / Тех.
                вопросы / Цен. вопросы / Договор заключён / Договор отложенный /
                Отложили строительство / В сопровождение / Новый заказ /
                Взял в работу / На монтаже / На остеклении / Заказ выполнен / …).
                Сортируем по количеству DESC. «Все» — всегда первый. */}
            {(() => {
              const counts = new Map<string, number>();
              results.forEach(h => {
                const cs = (h.canonical_stage || "").trim();
                if (!cs) return;
                counts.set(cs, (counts.get(cs) ?? 0) + 1);
              });
              const stages = Array.from(counts.entries())
                .sort((a, b) => b[1] - a[1])
                .map(([k]) => k);
              return (
                <div className="ntpm-stage-tabs">
                  <button
                    type="button"
                    className={`ntpm-stage-tab${stageFilter === "all" ? " is-active" : ""}`}
                    onClick={() => setStageFilter("all")}
                  >Все ({results.length})</button>
                  {stages.map(s => (
                    <button
                      key={s}
                      type="button"
                      className={`ntpm-stage-tab${stageFilter === s ? " is-active" : ""}`}
                      onClick={() => setStageFilter(s)}
                    >{s} ({counts.get(s)})</button>
                  ))}
                </div>
              );
            })()}
            <div className="ntpm-body">
              {loading && <div className="ntpm-empty">Поиск…</div>}
              {error && <div className="ntpm-error">{error}</div>}
              {!loading && !error && results.length === 0 && (
                <div className="ntpm-empty">
                  {debouncedQ ? `Ничего не найдено по «${debouncedQ}»` : "Сделок нет"}
                </div>
              )}
              {ensuring && <div className="ntpm-empty">Готовим сделку…</div>}
              {results
                .filter(h => {
                  if (stageFilter === "all") return true;
                  // Динамические чипы фильтруют по ТОЧНОМУ совпадению
                  // canonical_stage (название стадии Amo как есть).
                  return (h.canonical_stage || "").trim() === stageFilter;
                })
                .map(h => (
                <button
                  key={h.id != null ? `p${h.id}` : `a${h.amo_lead_id}`}
                  type="button"
                  className="ntpm-row"
                  disabled={ensuring}
                  onClick={() => onPick(h)}
                >
                  <div className="ntpm-row-icon">📦</div>
                  <div className="ntpm-row-text">
                    <div className="ntpm-row-name">{h.client_name}</div>
                    <div className="ntpm-row-sub">
                      {h.location && <span>{h.location}</span>}
                      {(() => {
                        // Приоритет: canonical_stage из Amo (Согласование выезда,
                        // Записаны на выезд, Выезд был, Договор заключён, ...).
                        // Fallback на STATUS_LABEL.
                        const label = h.canonical_stage || (h.status ? STATUS_LABEL[h.status] : null);
                        if (!label) return null;
                        const g = stageGroup(h.canonical_stage, h.status);
                        const cls = g ? `ntpm-status-stage-${g}` : "";
                        return <span className={`ntpm-status ${cls}`}>{label}</span>;
                      })()}
                      {h.amo_lead_id && <span className="ntpm-amo-small">Amo #{h.amo_lead_id}</span>}
                    </div>
                    {/* «Найдено по контакту: <имя>» — поясняет почему сделка
                        попала в результаты, когда сам name не содержит query
                        (Amo матчит и по контактам / custom fields). */}
                    {h.matched_contact && (
                      <div className="ntpm-row-matched-contact">
                        👤 найдено по контакту: <strong>{h.matched_contact}</strong>
                      </div>
                    )}
                  </div>
                  <Icon name="chevron_r" size={14}/>
                </button>
              ))}
            </div>
          </>
        ) : (
          <>
            <div
              className={`ntpm-body ntpm-compose-body${dragOver ? " is-drag" : ""}`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                const raw = Array.from(e.dataTransfer.files);
                if (raw.length) startUpload(raw);
              }}
            >
              <textarea
                ref={textareaRef}
                className="ntpm-textarea"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Текст примечания (можно перетащить файлы или Ctrl+V)…"
                rows={5}
                autoFocus
              />
              {files.length > 0 && (
                <div className="ntpm-files">
                  {files.map(f => {
                    const pct = f.total > 0 ? Math.round(f.loaded / f.total * 100) : 0;
                    return (
                      <div key={f.tempId} className={`ntpm-file${f.error ? " is-error" : f.attachmentId ? " is-done" : ""}`}>
                        <Icon name={f.error ? "x" : f.attachmentId ? "check" : "paperclip"} size={14}/>
                        <span className="ntpm-file-name">{f.file.name}</span>
                        <span className="ntpm-file-meta">
                          {f.error ? f.error : f.attachmentId ? "готово" : `${pct}%`}
                        </span>
                        <button type="button" className="ntpm-file-remove" onClick={() => removeFile(f.tempId)}>
                          <Icon name="x" size={12}/>
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="ntpm-attach-row" style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <button
                  type="button"
                  className="ntpm-attach-btn"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Icon name="paperclip" size={14}/> Прикрепить файл
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const raw = Array.from(e.target.files ?? []);
                    if (raw.length) startUpload(raw);
                    e.target.value = "";
                  }}
                />
                {picked?.amo_lead_id && (
                  <VoiceNoteRecorder
                    onSend={onVoiceSend}
                    disabled={submitting}
                    accent="#4fae4e"
                    idleTitle="Голосовое примечание (отправится сразу в Amo и закроет окно)"
                  />
                )}
                <span className="ntpm-attach-hint">или перетащи / Ctrl+V из буфера</span>
              </div>
              <label className={`ntpm-sync-toggle${!canSync ? " is-disabled" : ""}`}>
                <input
                  type="checkbox"
                  checked={syncAmo}
                  disabled={!canSync}
                  onChange={(e) => setSyncAmo(e.target.checked)}
                />
                <span>
                  Дублировать в AmoCRM
                  {!canSync && <small> (нет привязки к Amo-сделке)</small>}
                </span>
              </label>
            </div>
            <div className="ntpm-footer">
              <button type="button" className="ntpm-btn" onClick={() => setStep("pick")}>
                ← К списку
              </button>
              <button
                type="button"
                className="ntpm-btn ntpm-btn-primary"
                onClick={onSubmit}
                disabled={!hasContent || submitting || !allFilesUploaded}
                title={!allFilesUploaded ? "Подождите — файлы ещё загружаются" : ""}
              >
                {submitting ? "Отправка…" : !allFilesUploaded ? "Загрузка файлов…" : "Отправить"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
